import { useQuery } from '@tanstack/react-query';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

// ─── Builder Types ─────────────────────────────────────────────────────────────

export type SiteStatus = 'DRAFT' | 'GENERATING' | 'READY' | 'PUBLISHED' | 'FAILED';
export type BuilderTemplateId = 'degen' | 'clean' | 'meme';

export interface Site {
  id: string;
  projectId: string;
  contractAddress: string;
  narrative: string;
  templateId: string;
  subdomain: string;
  customDomain?: string;
  deploymentId?: string;
  deploymentUrl?: string;
  status: SiteStatus;
  tokenData: Record<string, unknown>;
  theme: Record<string, unknown>;
  sections: unknown[];
  content: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface PaginatedSites {
  data: Site[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Query Keys ────────────────────────────────────────────────────────────────

export const builderSiteKeys = {
  all: ['builderSites'] as const,
  lists: () => [...builderSiteKeys.all, 'list'] as const,
  list: (projectId: string) => [...builderSiteKeys.lists(), projectId] as const,
};

// ─── Hook ──────────────────────────────────────────────────────────────────────

/** Lists all sites for the current project */
export function useBuilderSites(): ReturnType<typeof useQuery<PaginatedSites>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: builderSiteKeys.list(projectId ?? ''),
    queryFn: async (): Promise<PaginatedSites> => {
      if (MOCK_MODE) {
        throw new Error(
          'Website Builder requires a live API connection. Set VITE_API_URL and disable VITE_MOCK_API for this feature.'
        );
      }
      return featureApi.get<PaginatedSites>(
        `/builder/sites?projectId=${encodeURIComponent(projectId ?? '')}`
      );
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2,
  });
}
